import TimelinePenerimaan from "@/components/penerimaan/TimelinePenerimaan";
import SectionHeader from "@/components/SectionHeader";
import Stepsbar from "@/components/Stepsbar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import useDataStep from "@/hooks/useDataStep";
import { useMultistepForm } from "@/hooks/useMultistepForm";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import api from "@/lib/axios";
import { formatDateIndonesia } from "@/lib/utils";
import { getDataStep } from "@/services/getData";
import { BiodataSate, DataSiswa, Step } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  ArrowRight,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  DoorClosed,
  Loader2,
  Megaphone,
  MessageCircle,
  Save,
} from "lucide-react";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import toast from "react-hot-toast";
import { Link, Navigate, useNavigate } from "react-router-dom";
import Biodata from "./partial/biodata/Biodata";
import KartuPeserta from "./partial/kartu-peserta/KartuPeserta";
import Portofolio from "./partial/portofolio/Portofolio";
import Prodi from "./partial/prodi/Prodi";
import Resume from "./partial/resume/Resume";
import UnggahBerkas from "./partial/unggah-berkas/UnggahBerkas";
import { web } from "@/config/System";

const saveBiodata = async (form: FormData) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/biodata/update",
    form,
    { withCredentials: true },
  );
  return response.data;
};

export default function PenerimaanCbtPage() {
  const navigate = useNavigate();
  const [sessionPeriode, setSessionPeriode] = useSessionStorage(
    "sessionPeriode",
    {
      id_sys_periode: "",
    },
  );

  const { isSudahFinalisasi, isValidasiPendaftaran } = useDataStep();
  const {
    isPendingPeriode,
    dataPeriode,
    timePassed,
    timePassedActive,
    dataPeriodeActive,
  } = useTimePeriode();

  const { data: dataStep } = useQuery({
    queryKey: ["dataStep", sessionPeriode?.id_sys_periode],
    queryFn: () => getDataStep(sessionPeriode?.id_sys_periode || ""),
  });

  const idDataPendaftaran = dataStep?.id_data_pendaftaran;
  const [isPendingSteps, setisPendingSteps] = useState(false);
  const [steps, setSteps] = useState<Step[]>([
    {
      number: 1,
      text: "Biodata",
      link: "#",
      status: "active",
      tabStatus: "active",
    },
    { number: null, text: null, link: null, status: "nonactive" },
    {
      number: 2,
      text: "Prodi",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 3,
      text: "Berkas",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 4,
      text: "Portofolio",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 5,
      text: "Rangkuman Pendaftaran",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
    {
      number: null,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 6,
      text: "Pembayaran & Kartu Peserta",
      link: "#",
      status: "nonactive",
      tabStatus: "nonactive",
    },
  ]);
  const [currentIndexDisable, setCurrentIndexDisable] = useState(1);

  const [formBiodata, setFormBiodata] = useState<BiodataSate>({
    id_sys_periode: sessionPeriode?.id_sys_periode || "",
    id_data_siswa: 0,
    id_ref_sekolah: "",
    id_ref_agama: "",
    id_ref_negara: "",
    id_ref_alat_transportasi: "",
    id_ref_jenis_tinggal: "",
    nisn: "",
    nama_lengkap: "",
    handphone: "",
    npwp: "",
    tanggal_lahir: "",
    tempat_lahir: "",
    jenis_kelamin: "L",
    berat_badan: 0,
    tinggi_badan: 0,
    ukuran_baju: "",
    jalan: "",
    rt: 0,
    rw: 0,
    kode_pos: "",
    kelurahan: "",
    provinsi: "",
    kabupaten: "",
    kecamatan: "",
    nama_panti_asuhan: "",
    kebutuhan_khusus: [] as string[],
    pilihan_lokasi_kuliah: "",
    is_alamat_sesuai_domisili: null,
    jalan_domisili: "",
    kelurahan_domisili: "",
    kecamatan_domisili: "",
    kabupaten_domisili: "",
    provinsi_domisili: "",
    kontak_darurat_hubungan: "",
    kontak_darurat_nama: "",
    kontak_darurat_hp: "",
  });
  const [file, setFile] = useState<File | null>(null);

  const queryClient = useQueryClient();
  const {
    mutate,
    isPending: isPendingSaveBiodata,
    error,
  } = useMutation({
    mutationFn: saveBiodata,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataStep"],
      });
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const {
    currentStep,
    firstStep,
    lastStep,
    backStep,
    goToStep,
    currentIndex,
    currentStepNumber,
  } = useMultistepForm([
    <Biodata
      form={formBiodata}
      setForm={setFormBiodata}
      file={file}
      setFile={setFile}
      error={error}
    />,
    <Prodi idDataPendaftaran={idDataPendaftaran} />,
    <UnggahBerkas idDataPendaftaran={idDataPendaftaran} />,
    <Portofolio idDataPendaftaran={idDataPendaftaran} />,
    <Resume />,
    <KartuPeserta />,
  ]);

  const handleSaveBiodata = async () => {
    const formData = new FormData();

    Object.keys(formBiodata).forEach((key) => {
      const value = formBiodata[key as keyof DataSiswa];
      if (Array.isArray(value)) {
        // Handle array (kebutuhan_khusus)
        value.forEach((item, index) => {
          if (item !== null && item !== undefined) {
            formData.append(`${key}[${index}]`, String(item)); // Konversi ke string menggunakan String()
          }
        });
      } else {
        // Handle nilai non-array
        if (value !== null && value !== undefined) {
          formData.append(key, String(value)); // Konversi ke string menggunakan String()
        }
      }
    });
    if (file) formData.append("foto", file);

    mutate(formData);
  };

  const handleNextStep = () => {
    switch (currentStepNumber) {
      case 1:
        if (dataStep?.data_diri === 1) {
          goToStep(2);
        }
        break;
      case 2:
        if (dataStep?.data_pilihan_prodi === 1) {
          goToStep(3);
        }
        break;
      case 3:
        if (dataStep?.data_berkas === 1) {
          goToStep(4);
        }
        break;
      case 4:
        if (dataStep?.data_portofolio === 1) {
          goToStep(5);
        }
        break;

      case 5:
        if (dataStep?.is_resume_pendaftaran === 1) {
          goToStep(6);
        }
        break;
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    setisPendingSteps(true);

    if (!dataStep) return;

    setSteps((prev) => {
      const updatedSteps = [...prev];

      // Helper function untuk update status connector (null steps)
      const updateConnectorStatus = (index: number) => {
        if (index <= 0 || index >= updatedSteps.length) return;

        const prevStep = updatedSteps[index - 1];
        if (updatedSteps[index].number === null) {
          updatedSteps[index].status =
            prevStep.status === "completed" ? "completed" : "nonactive";
        }
      };

      // Update main steps status
      updatedSteps.forEach((step, index) => {
        if (!step.text) return; // Skip connector steps

        switch (step.text) {
          case "Biodata":
            if (dataStep?.data_diri === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(2);
              setCurrentIndexDisable(2);
            }
            break;

          case "Prodi":
            if (dataStep?.data_pilihan_prodi === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(3);
              setCurrentIndexDisable(3);
            }
            break;
          case "Berkas":
            if (dataStep?.data_berkas === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(4);
              setCurrentIndexDisable(4);
            }
            break;
          case "Portofolio":
            if (dataStep?.data_portofolio === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(5);
              setCurrentIndexDisable(5);
            }
            break;
          case "Rangkuman Pendaftaran":
            if (dataStep?.is_resume_pendaftaran === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
              goToStep(6);
              setCurrentIndexDisable(6);
            }
            break;
          case "Pembayaran & Kartu Peserta":
            if (dataStep?.valid_is_pendaftaran === 1) {
              step.status = "completed";
              step.tabStatus = "active";
              if (updatedSteps[index + 2]) {
                updatedSteps[index + 2].status = "active";
                updatedSteps[index + 2].tabStatus = "active";
              }
            }
            break;
        }
      });

      // Update connector steps
      updatedSteps.forEach((_, index) => {
        updateConnectorStatus(index);
      });

      return updatedSteps;
    });

    setisPendingSteps(false);
  }, [goToStep, dataStep]);

  useEffect(() => {
    setSteps((prev) =>
      prev.map((item) => {
        if (item.number != currentStepNumber)
          return { ...item, tabStatus: "nonactive" };
        return { ...item, tabStatus: "active" };
      }),
    );
  }, [currentStepNumber]);

  useEffect(() => {
    if (timePassed.waktu_pengumuman_kelulusan) {
      flushSync(() => {
        setSessionPeriode({
          id_sys_periode: dataPeriode?.id_sys_periode || "",
        });
      });
      navigate(`/pengumuman-kelulusan`);
    }
    if (timePassed.waktu_pengumuman) {
      flushSync(() => {
        setSessionPeriode({
          id_sys_periode: dataPeriode?.id_sys_periode || "",
        });
      });
      navigate(`/pengumuman`);
    }
  }, [timePassed, navigate, sessionPeriode?.id_sys_periode]);

  const isDisableInput =
    isSudahFinalisasi ||
    isValidasiPendaftaran ||
    timePassedActive?.waktu_selesai_daftar_valid_selesai;

  if (
    dataPeriode &&
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug !== "cbt"
  ) {
    return <Navigate to="/dashboard" />;
  }

  const SHOW_TIMELINE =
    currentStepNumber !== 2 &&
    currentStepNumber !== 4 &&
    currentStepNumber !== 5 &&
    currentStepNumber !== 6;

  return (
    <main className="relative min-h-screen">
      <SectionHeader
        title={`Penerimaan - ${
          isPendingPeriode
            ? "Loading..."
            : dataPeriode?.jalur_jenis?.nama_jalur_jenis
        }`}
      />

      {/* --- BAGIAN 1: STATUS PENDAFTARAN DITUTUP --- */}
      {timePassedActive?.waktu_selesai_daftar_valid_selesai && (
        <Alert className="border-l-4 border-l-rose-500 bg-rose-50 dark:bg-rose-950/20 border-t border-r border-b border-rose-200 dark:border-rose-800">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-white dark:bg-rose-900 rounded-full shadow-sm mt-1">
              <DoorClosed className="h-6 w-6 text-rose-600 dark:text-rose-400" />
            </div>

            <div className="flex-1">
              <AlertTitle className="text-lg font-bold text-rose-800 dark:text-rose-300 flex flex-col sm:flex-row sm:items-center gap-2">
                Pendaftaran Ditutup
                <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-rose-200 dark:bg-rose-800 text-rose-800 dark:text-rose-200 w-fit">
                  Jalur {dataPeriode?.nama_jalur_jenis}
                </span>
              </AlertTitle>

              <AlertDescription className="mt-3 text-slate-700 dark:text-slate-300">
                <div className="grid gap-3">
                  {/* Info Timeline */}
                  <div className="bg-white/60 dark:bg-black/20 p-3 rounded-lg border border-rose-100 dark:border-rose-900 space-y-2 text-sm">
                    <div className="flex gap-2 items-start">
                      <CalendarClock className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
                      <span>
                        Pendaftaran & Validasi berakhir pada: <br />
                        <span className="font-semibold text-rose-700 dark:text-rose-400">
                          {dataPeriodeActive?.waktu_selesai_daftar_valid &&
                            formatDateIndonesia(
                              dataPeriodeActive.waktu_selesai_daftar_valid,
                            )}
                        </span>
                      </span>
                    </div>

                    <div className="flex gap-2 items-start">
                      <Megaphone className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
                      <span>
                        Pengumuman kelulusan pada: <br />
                        <span className="font-semibold text-rose-700 dark:text-rose-400">
                          {dataPeriode?.waktu_pengumuman_kelulusan &&
                            formatDateIndonesia(
                              dataPeriode.waktu_pengumuman_kelulusan,
                            )}
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Info Alternatif (Upselling Jalur Lain) */}
                  <div className="text-sm bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-100 dark:border-amber-800">
                    <p className="text-amber-800 dark:text-amber-200 mb-2">
                      <strong>Belum sempat mendaftar?</strong> Masih ada
                      kesempatan melalui Jalur Mandiri Nilai Rapor atau Talent
                      Scouting.
                    </p>
                    <Link to="/dashboard">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 bg-white border-amber-300 text-amber-700 hover:bg-amber-100 hover:text-amber-800 dark:bg-transparent dark:text-amber-400 dark:hover:bg-amber-900/50"
                      >
                        Cek Jalur Lain di Dashboard{" "}
                        <ArrowRight className="ml-2 h-3 w-3" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </AlertDescription>
            </div>
          </div>
        </Alert>
      )}

      {/* --- BAGIAN 2: TELEGRAM CTA (SELALU MUNCUL) --- */}
      <Alert className="mt-3 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 flex flex-col sm:flex-row items-center gap-4 py-3">
        <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-full text-blue-600 dark:text-blue-400">
          <MessageCircle className="h-5 w-5" />
        </div>
        <div className="flex-1 text-center sm:text-left">
          <AlertTitle className="text-blue-900 dark:text-blue-200 font-semibold text-sm mb-1">
            Grup Telegram Resmi SMBJM {dataPeriode?.tahun}
          </AlertTitle>
          <AlertDescription className="text-blue-700 dark:text-blue-300 text-xs">
            Dapatkan notifikasi pengumuman dan informasi penting secara{" "}
            <i>real-time</i>.
          </AlertDescription>
        </div>
        <Link to={web.linkGrupTelegramSMBJM} target="_blank">
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm shadow-blue-200 dark:shadow-none"
          >
            Gabung Grup
          </Button>
        </Link>
      </Alert>

      <br />
      <div className="flex lg:flex-col flex-row lg:gap-10">
        <Stepsbar
          steps={steps}
          goToStep={goToStep}
          isPending={isPendingSteps}
        />

        <div className="md:ml-0 ml-5 flex-1">
          <div className="flex lg:flex-row flex-col lg:gap-5 gap-3">
            {SHOW_TIMELINE && (
              <div>
                <TimelinePenerimaan />
              </div>
            )}

            <div className="flex-1">
              {currentStep}

              {/* Wrapper Navigation */}
              <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800 flex flex-col-reverse sm:flex-row justify-between items-center gap-4">
                {/* Tombol KIRI (Kembali) */}
                <div className="w-full sm:w-auto">
                  {!firstStep && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        backStep();
                        window.scrollTo({
                          top: 0,
                          behavior: "smooth",
                        });
                      }}
                      className="w-full sm:w-auto text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      Kembali
                    </Button>
                  )}
                </div>

                {/* Tombol KANAN (Group Aksi) */}
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  {/* Tombol Simpan (Hanya di step awal & bisa diedit) */}
                  {firstStep && !isDisableInput && (
                    <Button
                      type="button"
                      variant="secondary" // Menggunakan varian secondary agar tidak rebutan fokus dengan 'Berikutnya'
                      className="w-full sm:w-auto bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800"
                      disabled={isPendingSaveBiodata}
                      onClick={handleSaveBiodata}
                    >
                      {isPendingSaveBiodata ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Menyimpan...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Simpan
                        </>
                      )}
                    </Button>
                  )}

                  {!lastStep &&
                    (!firstStep || (firstStep && dataStep?.data_diri == 1)) && (
                      <Button
                        variant="default"
                        type="button"
                        className={`flex-1 sm:flex-none ${currentStepNumber == 4 && dataStep?.data_portofolio != 1 ? "hidden" : ""}`}
                        disabled={
                          isPendingSaveBiodata ||
                          currentIndex === currentIndexDisable - 1
                        }
                        onClick={handleNextStep}
                      >
                        Berikutnya
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
